import React from "react";
import { Progress } from "@shared/ui/progress";
import { Loader2, AlertCircle } from "lucide-react";

interface GenerationProgressProps {
  progress: number;
  totalCount: number;
  processed?: number;
  successful?: number;
  failed?: number;
  isError?: boolean;
  errorMessage?: string;
}

export const GenerationProgress = ({
  progress,
  totalCount,
  processed = 0,
  successful = 0,
  failed = 0,
  isError,
  errorMessage,
}: GenerationProgressProps) => {
  return (
    <div className="flex flex-col items-center justify-center py-16 space-y-8 animate-in fade-in zoom-in duration-500">
      {/* Centered Large Spinner or Error Icon */}
      <div className="relative flex items-center justify-center">
        <div
          className={`w-24 h-24 rounded-full border-8 ${
            isError ? "border-red-50" : "border-blue-50/50"
          }`}
        />
        {isError ? (
          <AlertCircle className="w-24 h-24 text-red-500 absolute" />
        ) : (
          <Loader2 className="w-24 h-24 text-blue-600 animate-spin absolute" />
        )}
      </div>

      {/* Title & Description */}
      <div className="text-center space-y-2">
        <h2
          className={`text-2xl font-bold tracking-tight ${
            isError ? "text-red-700" : "text-blue-800"
          }`}
        >
          {isError ? "Generation Interrupted" : "Generating Bills..."}
        </h2>
        <p
          className={`${
            isError ? "text-red-500" : "text-neutral-500"
          } font-medium max-w-md`}
        >
          {isError
            ? errorMessage || "An unexpected error occurred during tracking."
            : `Processing consumer bills. Please do not close this window.`}
        </p>
      </div>

      {/* Progress Bar Container */}
      {!isError && (
        <div className="w-full max-w-md space-y-4 pt-4">
          <div className="space-y-2">
            <Progress value={progress} className="h-2 bg-neutral-100" />
            <div className="flex justify-between text-xs font-bold text-neutral-400 uppercase tracking-widest">
              <span>{Math.round(progress)}% Complete</span>
              <span>
                Processed: {processed} / {totalCount}
              </span>
            </div>
          </div>

          {/* Detailed Stats */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-neutral-100">
            <div className="text-center">
              <p className="text-xs text-neutral-400 uppercase font-bold tracking-tighter">
                Successful
              </p>
              <p className="text-lg font-bold text-green-600">{successful}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-neutral-400 uppercase font-bold tracking-tighter">
                Failed
              </p>
              <p className="text-lg font-bold text-red-600">{failed}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
